import { useTheme } from "@emotion/react";
import {
  AccessAlarmOutlined,
  ContentCopy,
  ExpandMore,
  HistoryOutlined,
  LocalOfferOutlined,
  ReportOutlined,
} from "@mui/icons-material";
import {
  Card,
  CardActionArea,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import useStyles from "./styles";

export default function FluxoAtendimentoEtapaComponent(params) {
  const {
    title = "Fluxo de atendimento",
    color = "grey",
    setSelectEtapa = () => {},
    fluxo = {},
    selected = false,
    toggleDrawer = () => {},
    windowWidth = null,
    isPropertyColor = false,
    timeSla = null,
    timeSlaIdle = null,
    converterMinutos = () => {},
  } = params;

  const theme = useTheme();
  const styles = useStyles(theme);
  // `color` é um nome da paleta (HubSoft) ou já uma cor (IXC/Voalle);
  // desconhecido na paleta, usa como cor em vez de quebrar.
  const colorCard = isPropertyColor
    ? color
    : theme.palette.colors[color]?.medium ?? color;

  // Listas de estouro extraídas do fluxo
  const estouradosRegular = fluxo?.atendimentosSlaExcedido || [];
  const estouradosIdle = fluxo?.atendimentosSlaExcedidoIdle || [];

  // Mapeia e consolida todos os tickets que falharam em um dos dois SLAs para evitar duplicações no Tooltip
  const todosEstouradosMap = new Map();

  estouradosRegular.forEach((t) => {
    todosEstouradosMap.set(t.id_atendimento, { ...t, motivoRegular: true });
  });

  estouradosIdle.forEach((t) => {
    const existe = todosEstouradosMap.get(t.id_atendimento);
    if (existe) {
      existe.motivoIdle = true;
    } else {
      todosEstouradosMap.set(t.id_atendimento, { ...t, motivoIdle: true });
    }
  });

  const listaFinalEstourados = Array.from(todosEstouradosMap.values());
  const totalEstourados = listaFinalEstourados.length;

  return (
    <Card variant="outlined">
      <CardActionArea
        data-active={selected ? "" : undefined}
        onClick={() => {
          setSelectEtapa(fluxo);
          if (windowWidth < 750) {
            toggleDrawer(true);
          }
        }}
        sx={{
          p: 2,
          border: selected ? `2px solid ${colorCard}` : "none",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            width: "10px",
            height: "100%",
            backgroundColor: colorCard,
          },
          "&[data-active]": {
            backgroundColor: "action.selected",
            "&:hover": {
              backgroundColor: "action.selectedHover",
            },
          },
        }}
        expandIcon={<ExpandMore />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Box sx={styles.containerCard}>
          <Box sx={{ p: 0.5 }}>
            <Box sx={styles.boxCard}>
              <Typography
                sx={{ fontWeight: "bold", fontSize: "14px" }}
                component="span"
              >
                {title}
              </Typography>
            </Box>
            <Box sx={styles.containerValueSla}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocalOfferOutlined
                  sx={{ fontSize: "12px", color: "text.secondary" }}
                />
                <Typography sx={styles.titleDescricaoAlertSls}>
                  {fluxo?.atendimentos?.length} atendimento
                  {fluxo?.atendimentos?.length > 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={styles.containerDescricaoCard}>
            {/* 🕒 SEÇÃO: Exibição dos Tempos Limites Configurados (SLA da Etapa e SLA Idle) */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              {timeSla && (
                <Tooltip
                  placement="top"
                  arrow
                  title={`Tempo limite de atendimento na etapa: ${timeSla}`}
                >
                  <Box sx={styles.boxTimeSla}>
                    <AccessAlarmOutlined sx={styles.icon} />
                    <Typography sx={styles.valueSla}>{timeSla}</Typography>
                  </Box>
                </Tooltip>
              )}

              {timeSlaIdle && (
                <Tooltip
                  placement="top"
                  arrow
                  title={`Tempo limite de inatividade: ${timeSlaIdle}`}
                >
                  <Box sx={styles.boxTimeSla}>
                    <HistoryOutlined sx={{ ...styles.icon }} />
                    <Typography sx={styles.valueSla}>{timeSlaIdle}</Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>

            <Box>
              {totalEstourados > 0 && (
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <Box sx={{ maxWidth: 320 }}>
                      <Box sx={{ p: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "common.white",
                          }}
                        >
                          Atendimentos Excedidos
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          ...styles.boxAtendimentoEstourados,
                        }}
                      >
                        {listaFinalEstourados.map((ticket, index) => (
                          <Paper
                            variant="outlined"
                            key={index}
                            sx={{
                              ...styles.paperAtendimentoEstourado,
                            }}
                          >
                            {/* PROTOCOLO E TAGS DO MOTIVO */}
                            <Box sx={styles.boxProtocolo}>
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 600,
                                }}
                              >
                                Protocolo:{" "}
                                <Box
                                  component="span"
                                  sx={{ color: theme.palette.primary.main }}
                                >
                                  {ticket?.protocolo}
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(
                                        ticket?.protocolo,
                                      );
                                    } catch (err) {
                                      // Fallback para navegadores mais antigos ou sem permissão
                                      const textArea =
                                        document.createElement("textarea");
                                      textArea.value = ticket?.protocolo;
                                      document.body.appendChild(textArea);
                                      textArea.select();
                                      try {
                                        document.execCommand("copy");
                                      } catch (err) {
                                        console.error(
                                          "Falha ao copiar texto: ",
                                          err,
                                        );
                                      }
                                      document.body.removeChild(textArea);
                                    }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: "12px" }} />
                                </IconButton>
                              </Typography>
                            </Box>

                            {/* CLIENTE */}
                            <Typography
                              sx={{
                                fontSize: "11px",
                                ...styles.typographyCliente,
                              }}
                            >
                              {ticket?.cliente?.nome}
                            </Typography>

                            {/* INDICADORES DE TEMPO INTERNOS DO TICKET */}
                            <Box sx={styles.boxTimes}>
                              {ticket?.motivoRegular && (
                                <Typography sx={{ fontSize: "10px" }}>
                                  • Tempo na etapa:{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "error.light",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {converterMinutos(ticket?.timeSla)?.text}
                                  </Box>
                                </Typography>
                              )}
                              {ticket.motivoIdle && (
                                <Typography sx={{ fontSize: "10px" }}>
                                  • Tempo sem atualização:{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "warning.main",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {
                                      converterMinutos(ticket?.timeSlaIdle)
                                        ?.text
                                    }
                                  </Box>
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      ...styles.containerDataSla,
                    }}
                  >
                    <Box
                      sx={{
                        ...styles.boxAlertSla,
                      }}
                    >
                      <ReportOutlined
                        sx={{
                          ...styles.iconAlertSla,
                          color: theme.palette.warning.main,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        ...styles.titleDescricaoAlertSls,
                        fontWeight: 600,
                        color: theme.palette.warning.main,
                      }}
                    >
                      {`${totalEstourados} ${totalEstourados > 1 ? "tickets" : "ticket"}`}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}
